import { normalizeOverlayStyle } from '@/lib/overlayStyle';
import { rewriteMapStyleSourcesToRuntime } from '@workspace/ui/src/utils/map-style-sources';

export type CanonicalShareScope = 'tenant' | 'shared' | 'public';

export interface SharedMappingAsset {
  id?: string;
  name?: string;
  description?: string | null;
  tenant_id?: string | null;
  share_scope?: string | null;
  updated_at?: string | null;
}

export interface MapCustomMapConfig {
  version?: number;
  initial_view?: {
    center?: [number, number];
    zoom?: number;
    bearing?: number;
    pitch?: number;
  } | null;
  preset_summary?: {
    layer_count?: number;
    rule_count?: number;
  } | null;
}

export interface MappingDependencyRef {
  id: string;
  name: string;
  share_scope: CanonicalShareScope;
}

export interface MappingSourceDependencyRef extends MappingDependencyRef {
  alias: string;
}

export interface MappingSpriteDependencyRef {
  id: string;
  sprite_set_id: string;
  name: string;
  share_scope: CanonicalShareScope;
}

export interface MapCompositionMetadata extends Record<string, unknown> {
  asset_kind?: 'custom-map';
  custom_map?: MapCustomMapConfig | null;
  map_experience?: Record<string, unknown> | null;
  overlay_style?: Record<string, unknown> | null;
  source_refs?: MappingSourceDependencyRef[];
  sprite_refs?: MappingSpriteDependencyRef[];
  active_sprite_id?: string | null;
  sprite_ref?: MappingDependencyRef | null;
  glyph_ref?: MappingDependencyRef | null;
}

export interface ShareScopeOption {
  value: CanonicalShareScope;
  label: string;
  description: string;
}

export interface MappingAssetSalesContext {
  catalog_owner_tenant_id?: string | null;
  entitlement_id?: string | null;
}

export const SHARE_SCOPE_OPTIONS: ShareScopeOption[] = [
  {
    value: 'tenant',
    label: 'Tenant',
    description: 'Only the owning tenant can use this asset.'
  },
  {
    value: 'shared',
    label: 'Shared',
    description: 'Share with a specific set of tenant IDs.'
  },
  {
    value: 'public',
    label: 'Public',
    description: 'Visible to any tenant that can access mapping.'
  }
];

export function normalizeShareScope(value?: string | null): CanonicalShareScope {
  switch ((value ?? '').trim().toLowerCase()) {
    case 'public':
      return 'public';
    case 'shared':
      return 'shared';
    case 'private':
    case 'tenant':
    default:
      return 'tenant';
  }
}

export function formatShareScopeLabel(value?: string | null): string {
  return normalizeShareScope(value);
}

const countCanonicalRules = (mapExperience: Record<string, unknown> | null | undefined): { layerCount: number; ruleCount: number } => {
  const styleProfiles = Array.isArray((mapExperience as { style_profiles?: unknown[] } | null | undefined)?.style_profiles)
    ? (((mapExperience as { style_profiles: unknown[] }).style_profiles) ?? [])
    : [];
  const rulesProfiles = styleProfiles.filter(
    (profile): profile is { kind?: unknown; rules?: unknown[] } =>
      Boolean(profile) && typeof profile === 'object' && (profile as { kind?: unknown }).kind === 'rules',
  );
  return {
    layerCount: rulesProfiles.length,
    ruleCount: rulesProfiles.reduce((total, profile) => total + (Array.isArray(profile.rules) ? profile.rules.length : 0), 0),
  };
};

export function isOwnedByCurrentTenant(assetTenantId?: string | null, featureTenantId?: string): boolean {
  if (!assetTenantId || !featureTenantId) {
    return false;
  }
  return assetTenantId === featureTenantId;
}

export function isPortalAssetReadOnly(assetTenantId?: string | null, featureTenantId?: string): boolean {
  return !isOwnedByCurrentTenant(assetTenantId, featureTenantId);
}

export function isCustomMapAsset(asset?: { metadata?: Record<string, unknown> | null } | null): boolean {
  return asset?.metadata?.asset_kind === 'custom-map';
}

export function getCustomMapConfig(asset?: { metadata?: Record<string, unknown> | null } | null): MapCustomMapConfig | null {
  if (!isCustomMapAsset(asset)) {
    return null;
  }
  const customMap = asset?.metadata?.custom_map;
  if (!customMap || typeof customMap !== 'object') {
    return null;
  }
  return customMap as MapCustomMapConfig;
}

export function formatViewportSummary(
  view?: {
    center?: [number, number];
    zoom?: number;
  } | null
): string {
  if (!view?.center || typeof view.zoom !== 'number') {
    return 'No saved viewport';
  }
  const [lng, lat] = view.center;
  return `${lat.toFixed(3)}, ${lng.toFixed(3)} · z${view.zoom.toFixed(1)}`;
}

export function buildSpriteRuntimeBaseUrl(id?: string): string {
  if (!id) {
    return '';
  }
  return `/api/v1/mapping/sprite-sets/${id}/sprite`;
}

export function buildSpriteSchemeUrl(id?: string): string {
  return id ? `sprite://${id}` : '';
}

export function parseSpriteDependencyRefsFromStyleRoot(
  value?: unknown,
): Array<{ id: string; sprite_set_id: string }> {
  if (typeof value === 'string') {
    const spriteSetId = parseSpriteSetIdFromStyleUrl(value);
    return spriteSetId ? [{ id: 'default', sprite_set_id: spriteSetId }] : [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const spriteId = typeof record.id === 'string' ? record.id.trim() : '';
      const spriteSetId = parseSpriteSetIdFromStyleUrl(record.url);
      if (!spriteId || !spriteSetId) {
        return null;
      }
      return {
        id: spriteId,
        sprite_set_id: spriteSetId,
        sort_order: index,
      };
    })
    .filter((entry): entry is { id: string; sprite_set_id: string; sort_order: number } => Boolean(entry))
    .sort((left, right) => left.sort_order - right.sort_order)
    .map(({ id, sprite_set_id }) => ({ id, sprite_set_id }));
}

export function buildSpriteStyleRootValue(
  spriteRefs?: Array<{ id?: string; sprite_set_id?: string }> | null,
): string | Array<{ id: string; url: string }> | undefined {
  const normalized = (spriteRefs ?? [])
    .map(ref => ({
      id: typeof ref.id === 'string' ? ref.id.trim() : '',
      sprite_set_id: typeof ref.sprite_set_id === 'string' ? ref.sprite_set_id.trim() : '',
    }))
    .filter(ref => ref.id.length > 0 && ref.sprite_set_id.length > 0);
  if (!normalized.length) {
    return undefined;
  }
  if (normalized.length === 1 && normalized[0].id === 'default') {
    return buildSpriteSchemeUrl(normalized[0].sprite_set_id);
  }
  return normalized.map(ref => ({
    id: ref.id,
    url: buildSpriteSchemeUrl(ref.sprite_set_id),
  }));
}

export function buildGlyphRuntimeUrl(id?: string): string {
  if (!id) {
    return '';
  }
  return `/api/v1/mapping/glyph-sets/${id}/{fontstack}/{range}`;
}

export function buildGlyphSchemeUrl(id?: string): string {
  return id ? `glyphs://${id}` : '';
}

export function buildMapSourceArchiveUrl(id?: string, context?: MappingAssetSalesContext | null): string {
  return buildMapSourceRuntimeUrl(id, 'archive', context);
}

export function buildMapSourceGeoJSONDataUrl(id?: string, context?: MappingAssetSalesContext | null): string {
  return buildMapSourceRuntimeUrl(id, 'data.geojson', context);
}

function buildMapSourceRuntimeUrl(
  id: string | undefined,
  suffix: 'archive' | 'data.geojson',
  context?: MappingAssetSalesContext | null,
): string {
  if (!id) {
    return '';
  }
  const path = `/api/v1/mapping/map-sources/${encodeURIComponent(id)}/${suffix}`;
  if (!context?.catalog_owner_tenant_id) {
    return path;
  }
  const query = new URLSearchParams();
  query.set('sales_context_catalog_owner_tenant_id', context.catalog_owner_tenant_id);
  if (context.entitlement_id) {
    query.set('sales_context_entitlement_id', context.entitlement_id);
  }
  return `${path}?${query.toString()}`;
}

const PMTILES_PREFIX = 'pmtiles://';
const GEOJSON_PREFIX = 'geojson://';

export function parseSpriteSetIdFromStyleUrl(value?: unknown): string | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  if (value.startsWith('sprite://')) {
    return value.slice('sprite://'.length).split(/[/?#]/, 1)[0] || null;
  }
  const match = value.match(/\/sprite-sets\/([^/]+)\/sprite(?:$|[/?#])/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function normalizeSpriteDependencyRefs(
  metadata?: MapCompositionMetadata | null,
  styleJson?: Record<string, unknown> | null,
): MappingSpriteDependencyRef[] {
  const refs = new Map<string, MappingSpriteDependencyRef>();
  if (Array.isArray(metadata?.sprite_refs)) {
    metadata?.sprite_refs.forEach(entry => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const record = entry as MappingSpriteDependencyRef;
      const spriteId = typeof record.id === 'string' ? record.id.trim() : '';
      const spriteSetId = typeof record.sprite_set_id === 'string' ? record.sprite_set_id.trim() : '';
      if (!spriteId || !spriteSetId || refs.has(spriteId)) {
        return;
      }
      refs.set(spriteId, {
        id: spriteId,
        sprite_set_id: spriteSetId,
        name: record.name ?? spriteSetId,
        share_scope: normalizeShareScope(record.share_scope),
      });
    });
  }
  parseSpriteDependencyRefsFromStyleRoot(styleJson?.sprite).forEach(entry => {
    if (refs.has(entry.id)) {
      return;
    }
    refs.set(entry.id, {
      id: entry.id,
      sprite_set_id: entry.sprite_set_id,
      name: entry.sprite_set_id,
      share_scope: 'tenant',
    });
  });
  return Array.from(refs.values());
}

function cloneRecord<T extends Record<string, unknown>>(value?: T | null): T | null {
  if (!value) {
    return null;
  }
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

function collectStyleSourceRefs(
  styleJson: Record<string, unknown>,
  sources: Array<{ id?: string; name?: string; share_scope?: string | null }>
): MappingSourceDependencyRef[] {
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  return Object.entries(styleSources)
    .map(([alias, source]) => {
      const url = typeof source?.url === 'string' ? source.url : '';
      const data = typeof source?.data === 'string' ? source.data : '';
      const sourceId = url.startsWith('pmtiles://')
        ? url.slice('pmtiles://'.length)
        : data.startsWith('geojson://')
          ? data.slice('geojson://'.length)
          : '';
      if (!sourceId) {
        return null;
      }
      const entry = sources.find(candidate => candidate.id === sourceId);
      return {
        id: sourceId,
        alias,
        name: entry?.name ?? sourceId,
        share_scope: normalizeShareScope(entry?.share_scope),
      };
    })
    .filter((entry): entry is MappingSourceDependencyRef => Boolean(entry));
}

function extractSourceIdFromResolvableSource(source?: Record<string, unknown> | null): string {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const url = typeof source.url === 'string' ? source.url.trim() : '';
  if (url.startsWith(PMTILES_PREFIX)) {
    return url.slice(PMTILES_PREFIX.length);
  }

  const data = typeof source.data === 'string' ? source.data.trim() : '';
  if (data.startsWith(GEOJSON_PREFIX)) {
    return data.slice(GEOJSON_PREFIX.length);
  }

  const archiveMatch = url.match(/^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/archive(?:$|[?#])/);
  if (archiveMatch?.[1]) {
    return decodeURIComponent(archiveMatch[1]);
  }

  const geoJSONMatch = data.match(/^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson(?:$|[?#])/);
  if (geoJSONMatch?.[1]) {
    return decodeURIComponent(geoJSONMatch[1]);
  }

  const tiles = Array.isArray(source.tiles) ? source.tiles : [];
  const tileTemplate = tiles.find((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  const tileMatch = tileTemplate?.match(/^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/tiles\/\{z\}\/\{x\}\/\{y\}(?:$|[?#])/);
  return tileMatch?.[1] ? decodeURIComponent(tileMatch[1]) : '';
}

function inferSourceRefsFromStyleLayers(styleJson?: Record<string, unknown> | null): MappingSourceDependencyRef[] {
  const layers = Array.isArray(styleJson?.layers) ? styleJson.layers : [];
  const refs = new Map<string, MappingSourceDependencyRef>();

  layers.forEach(layer => {
    if (!layer || typeof layer !== 'object') {
      return;
    }
    const record = layer as Record<string, unknown>;
    const alias = typeof record.source === 'string' ? record.source.trim() : '';
    if (!alias || refs.has(alias)) {
      return;
    }
    const metadata =
      record.metadata && typeof record.metadata === 'object' ? (record.metadata as Record<string, unknown>) : null;
    const workbench =
      metadata?.workbench && typeof metadata.workbench === 'object'
        ? (metadata.workbench as Record<string, unknown>)
        : null;
    const sourceId =
      (typeof workbench?.source_id === 'string' && workbench.source_id.trim()) ||
      (typeof workbench?.sourceId === 'string' && workbench.sourceId.trim()) ||
      '';
    if (!sourceId) {
      return;
    }
    refs.set(alias, {
      id: sourceId,
      alias,
      name:
        (typeof workbench?.source_alias === 'string' && workbench.source_alias.trim()) ||
        (typeof workbench?.sourceAlias === 'string' && workbench.sourceAlias.trim()) ||
        alias,
      share_scope: normalizeShareScope(
        typeof workbench?.share_scope === 'string' ? workbench.share_scope : null
      ),
    });
  });

  return Array.from(refs.values());
}

function resolveSourceDependencyRefs(
  styleJson?: Record<string, unknown> | null,
  sourceRefs?: unknown,
): MappingSourceDependencyRef[] {
  const refs = new Map<string, MappingSourceDependencyRef>();

  if (Array.isArray(sourceRefs)) {
    sourceRefs.forEach(entry => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const record = entry as Record<string, unknown>;
      const alias = typeof record.alias === 'string' ? record.alias.trim() : '';
      const sourceId =
        (typeof record.id === 'string' && record.id.trim()) ||
        (typeof record.source_id === 'string' && record.source_id.trim()) ||
        '';
      if (!alias || !sourceId) {
        return;
      }
      refs.set(alias, {
        id: sourceId,
        alias,
        name:
          (typeof record.name === 'string' && record.name.trim()) ||
          (typeof record.source_name === 'string' && record.source_name.trim()) ||
          alias,
        share_scope: normalizeShareScope(typeof record.share_scope === 'string' ? record.share_scope : null),
      });
    });
  }

  const styleSources =
    styleJson?.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  Object.entries(styleSources).forEach(([alias, source]) => {
    if (refs.has(alias)) {
      return;
    }
    const sourceId = extractSourceIdFromResolvableSource(source);
    if (!sourceId) {
      return;
    }
    refs.set(alias, {
      id: sourceId,
      alias,
      name: alias,
      share_scope: 'tenant',
    });
  });

  inferSourceRefsFromStyleLayers(styleJson).forEach(ref => {
    if (!refs.has(ref.alias)) {
      refs.set(ref.alias, ref);
    }
  });

  return Array.from(refs.values());
}

export function buildOverlayStyleFromRuntime(
  styleJson?: Record<string, unknown> | null,
  metadata?: MapCompositionMetadata | null,
) {
  const fallback = normalizeOverlayStyle(cloneRecord(styleJson));
  const preferResolvedStyle =
    Boolean(metadata?.managed_version) ||
    Boolean(metadata?.required_source_contract) ||
    (typeof metadata?.managed_by === 'string' && metadata.managed_by.trim().length > 0);
  const authored = preferResolvedStyle ? null : normalizeOverlayStyle(cloneRecord(metadata?.overlay_style));
  const nextStyle = authored ?? fallback;
  if (!nextStyle) {
    return null;
  }

  const sourceRefs = resolveSourceDependencyRefs(nextStyle, metadata?.source_refs);
  const styleSources =
    nextStyle.sources && typeof nextStyle.sources === 'object'
      ? (nextStyle.sources as Record<string, Record<string, unknown>>)
      : null;

  if (styleSources) {
    sourceRefs.forEach(ref => {
      if (!ref?.alias || !ref.id) {
        return;
      }
      const source = styleSources[ref.alias];
      if (!source || typeof source !== 'object') {
        return;
      }
      if (typeof source.data === 'string' || source.type === 'geojson') {
        styleSources[ref.alias] = {
          ...source,
          type: 'geojson',
          data: `geojson://${ref.id}`,
        };
        delete styleSources[ref.alias].tiles;
        delete styleSources[ref.alias].url;
        return;
      }
      styleSources[ref.alias] = {
        ...source,
        type: 'vector',
        url: `pmtiles://${ref.id}`,
      };
      delete styleSources[ref.alias].tiles;
      delete styleSources[ref.alias].data;
    });
  }

  const spriteRefs = normalizeSpriteDependencyRefs(metadata, nextStyle);
  const spriteRoot = buildSpriteStyleRootValue(spriteRefs);
  if (spriteRoot !== undefined) {
    nextStyle.sprite = spriteRoot;
  }

  const glyphId = metadata?.glyph_ref?.id ?? parseGlyphSetIdFromStyleUrl(nextStyle.glyphs);
  if (glyphId) {
    nextStyle.glyphs = buildGlyphSchemeUrl(glyphId);
  }

  return nextStyle;
}

export function rewritePreviewStyleToUseSourceArchives(
  styleJson?: Record<string, unknown> | null,
  context?: MappingAssetSalesContext | null,
) {
  const nextStyle = rewriteMapStyleSourcesToRuntime(styleJson, {
    buildMapSourceArchiveUrl: id => buildMapSourceArchiveUrl(id, context),
    buildMapSourceGeoJSONDataUrl: id => buildMapSourceGeoJSONDataUrl(id, context),
  });
  if (!nextStyle) {
    return nextStyle;
  }
  return nextStyle;
}

function toDependencyRef(asset?: { id?: string; name?: string; share_scope?: string | null } | null): MappingDependencyRef | null {
  if (!asset?.id) {
    return null;
  }
  return {
    id: asset.id,
    name: asset.name ?? asset.id,
    share_scope: normalizeShareScope(asset.share_scope),
  };
}

function toSpriteDependencyRefs(
  sprites?: Array<{ id?: string; sprite_set_id?: string; name?: string; share_scope?: string | null }> | null,
): MappingSpriteDependencyRef[] {
  return (sprites ?? [])
    .map(sprite => {
      const spriteId = typeof sprite?.id === 'string' ? sprite.id.trim() : '';
      const spriteSetId = typeof sprite?.sprite_set_id === 'string' ? sprite.sprite_set_id.trim() : '';
      if (!spriteId || !spriteSetId) {
        return null;
      }
      return {
        id: spriteId,
        sprite_set_id: spriteSetId,
        name: sprite.name ?? spriteSetId,
        share_scope: normalizeShareScope(sprite.share_scope),
      };
    })
    .filter((entry): entry is MappingSpriteDependencyRef => Boolean(entry));
}

export function buildCompositionMetadata({
  existingMetadata,
  styleJson,
  authoredStyleJson,
  sources,
  sprite,
  sprites,
  activeSpriteId,
  glyph,
  mapExperience,
  customMap,
  initialView,
}: {
  existingMetadata?: MapCompositionMetadata | null;
  styleJson: Record<string, unknown>;
  authoredStyleJson?: Record<string, unknown> | null;
  sources: Array<{ id?: string; name?: string; share_scope?: string | null }>;
  sprite?: { id?: string; name?: string; share_scope?: string | null } | null;
  sprites?: Array<{ id?: string; sprite_set_id?: string; name?: string; share_scope?: string | null }> | null;
  activeSpriteId?: string | null;
  glyph?: { id?: string; name?: string; share_scope?: string | null } | null;
  mapExperience?: Record<string, unknown> | null;
  customMap: boolean;
  initialView?: {
    center?: [number, number];
    zoom?: number;
    bearing?: number;
    pitch?: number;
  } | null;
}): MapCompositionMetadata {
  const { layerCount, ruleCount } = countCanonicalRules(mapExperience);
  const spriteRefs = toSpriteDependencyRefs(sprites);
  const resolvedActiveSpriteId =
    (typeof activeSpriteId === 'string' && activeSpriteId.trim()) ||
    spriteRefs[0]?.id ||
    null;
  const resolvedLegacySpriteRef =
    sprite ??
    (() => {
      const activeRef = spriteRefs.find(entry => entry.id === resolvedActiveSpriteId) ?? spriteRefs[0] ?? null;
      return activeRef
        ? {
            id: activeRef.sprite_set_id,
            name: activeRef.name,
            share_scope: activeRef.share_scope,
          }
        : null;
    })();

  return {
    ...(existingMetadata ?? {}),
    ...(customMap
      ? {
          asset_kind: 'custom-map',
          custom_map: {
            version: 1,
            initial_view: initialView ?? null,
            preset_summary: {
              layer_count: layerCount,
              rule_count: ruleCount,
            },
          },
        }
      : {}),
    ...(customMap ? {} : { asset_kind: undefined, custom_map: undefined }),
    ...(mapExperience === undefined
      ? {}
      : { map_experience: cloneRecord(mapExperience) ?? undefined }),
    overlay_style: cloneRecord(authoredStyleJson ?? styleJson),
    source_refs: collectStyleSourceRefs(styleJson, sources),
    sprite_refs: spriteRefs,
    active_sprite_id: resolvedActiveSpriteId,
    sprite_ref: toDependencyRef(resolvedLegacySpriteRef),
    glyph_ref: toDependencyRef(glyph),
  };
}

export function parseGlyphSetIdFromStyleUrl(value?: unknown): string | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  if (value.startsWith('glyphs://')) {
    return value.slice('glyphs://'.length).split('/', 1)[0] || null;
  }
  const match = value.match(/\/glyph-sets\/([^/]+)\/\{fontstack\}\/\{range\}(?:\.pbf)?(?:$|[?#])/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function parseTenantIds(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map(entry => entry.trim())
        .filter(Boolean)
    )
  );
}

export function formatTenantIds(tenantIds: string[]): string {
  return tenantIds.join('\n');
}
